import { zodResolver } from '@hookform/resolvers/zod'
import { LinkIcon, PlusIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  isPmsLookupProvider,
  PmsLookupProvider,
  PmsProvider,
  useConnectPms,
  useGetPmsCompanies,
  useGetPmsProperties,
} from '@/api/pms'
import { SelectFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { FieldRow, FieldsTable, FieldValue } from '@/components/layout/field-row'
import { TabCard } from '@/components/layout/tab-card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { SelectOption } from '@/components/ui/select'
import { API_ERRORS } from '@/constants'
import { popupFactory, toastFactory } from '@/factories'
import { Building } from '@/reducers/types'
import { isProductionBuild } from '@/utils/environment'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'

import { PmsCompanyFormModal } from './pms-company-form-modal'
import {
  getPmsTypeOptions,
  makePmsConnectSchema,
  PmsConnectFormData,
  PmsConnectProvider,
  PmsConnectProviderSchema,
} from './pms-tab.constants'

interface PmsConnectFormProps {
  building: Building
}

export const PmsConnectForm = ({ building }: PmsConnectFormProps) => {
  const { t } = useTranslation(['organisation'])
  const { mutateAsync: connect, isPending } = useConnectPms()

  const schema = useMemo(() => makePmsConnectSchema(t), [t])
  const typeOptions = useMemo(() => getPmsTypeOptions(t), [t])

  const form = useForm<PmsConnectFormData>({
    defaultValues: { company: undefined, property: '', type: undefined },
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const type = form.watch('type')
  const company = form.watch('company')
  const lookupProvider = isPmsLookupProvider(type) ? (type as PmsLookupProvider) : null

  const { data: companies = [], isLoading: isCompaniesLoading } = useGetPmsCompanies(type)
  const { data: properties = [], isLoading: isPropertiesLoading } = useGetPmsProperties(
    lookupProvider,
    company,
  )

  const previousType = useRef(type)
  useEffect(() => {
    if (previousType.current === type) return
    previousType.current = type
    form.setValue('company', undefined as never)
    form.setValue('property', '')
  }, [type, form])

  const companyOptions = useMemo<SelectOption<number>[]>(
    () => companies.map((item) => ({ label: item.name, value: item.id })),
    [companies],
  )
  const propertyOptions = useMemo<SelectOption<string>[]>(
    () => properties.map((item) => ({ label: item.name, value: String(item.id) })),
    [properties],
  )

  const handleAddCompany = () => {
    if (!PmsConnectProviderSchema.safeParse(type).success) return

    popupFactory.open(PmsCompanyFormModal, {
      onCreated: (created) => {
        form.setValue('company', created.id, { shouldDirty: true, shouldValidate: true })
        form.setValue('property', '')
      },
      provider: type as PmsConnectProvider,
    })
  }

  const handleConnect = form.handleSubmit(async (values) => {
    try {
      await connect({
        buildingId: building.id,
        companyId: values.company,
        propertyId: values.property,
        provider: values.type,
      })
      toastFactory.success(t('organisation:places.pms.toasts.connected'))
    } catch (error) {
      // Note: the backend refuses PMS connections on staging with a bare 403
      const isStagingBlock =
        !isProductionBuild && getApiErrorStatus(error as Error) === API_ERRORS.FORBIDDEN
      toastFactory.error(
        isStagingBlock
          ? t('organisation:places.pms.toasts.connectDisabledOnStaging')
          : getApiErrorMessage(error),
      )
    }
  })

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <Button loading={isPending} size="small" onClick={handleConnect}>
            <Icon icon={LinkIcon} />
            {t('organisation:places.pms.connect')}
          </Button>
        }
        title={t('organisation:places.integrations.pms.title')}
      >
        <FieldsTable>
          <FieldRow
            required
            label={t('organisation:places.integrations.pms.integrationType.label')}
          >
            <SelectFormField<PmsConnectFormData, PmsProvider>
              name="type"
              options={typeOptions}
              placeholder={t('organisation:places.integrations.pms.integrationType.placeholder')}
              size="small"
              type="single"
            />
          </FieldRow>

          {type && (
            <FieldRow required label={t('organisation:places.pms.connected.company')}>
              <FieldValue>
                <SelectFormField<PmsConnectFormData, number>
                  searchable
                  loading={isCompaniesLoading}
                  name="company"
                  options={companyOptions}
                  placeholder={t('organisation:places.integrations.pms.companyPlaceholder')}
                  size="small"
                  type="single"
                />
                <Button size="small" use="secondary" onClick={handleAddCompany}>
                  <Icon icon={PlusIcon} />
                  {t('organisation:places.integrations.pms.addNewCompany')}
                </Button>
              </FieldValue>
            </FieldRow>
          )}

          {type && company && lookupProvider && (
            <FieldRow required label={t('organisation:places.integrations.pms.property.label')}>
              <SelectFormField<PmsConnectFormData, string>
                searchable
                loading={isPropertiesLoading}
                name="property"
                options={propertyOptions}
                placeholder={t('organisation:places.integrations.pms.property.placeholder')}
                size="small"
                type="single"
              />
            </FieldRow>
          )}

          {type && company && !lookupProvider && (
            <FieldRow required label={t('organisation:places.integrations.pms.propertyId.label')}>
              <TextInputFormField
                name="property"
                placeholder={t('organisation:places.integrations.pms.propertyId.placeholder')}
                size="small"
              />
            </FieldRow>
          )}
        </FieldsTable>
      </TabCard>
    </FormProvider>
  )
}
